/**
 * File: App.jsx
 * Path: /src/App.jsx
 * Description: Main application component that defines routes and includes the initial loading animation.
 * Key Features: Route definitions, page transitions with AnimatePresence, and loading state.
 * Maintenance Notes: Update when adding new pages or changing route structure.
 */

import React, { useState, useEffect } from 'react';
import { Routes, Route, useLocation } from 'react-router-dom';
import { AnimatePresence } from 'framer-motion';

// Layouts
import MainLayout from './components/layout/MainLayout';

// Pages
import Home from './pages/Home';
import About from './pages/About';
import Contact from './pages/Contact';
import LegalResearch from './pages/LegalResearch';
import ContractAnalysis from './pages/ContractAnalysis';
import JudgmentPrediction from './pages/JudgmentPrediction';
import LegalChronologies from './pages/LegalChronologies';
import OCRTranslationPage from './pages/OCRTranslationPage';
import CaseManagement from './pages/CaseManagement';
import Careers from './pages/Careers';
import Blog from './pages/Blog';
import BlogPost from './pages/BlogPost';

// Custom Loader
const Loader = () => (
  <div className="fixed inset-0 flex items-center justify-center bg-white z-50">
    <div className="relative">
      <div className="loader"></div>
      <div className="absolute inset-0 flex items-center justify-center">
        <span className="text-primary-600 font-display font-bold">V</span>
      </div>
    </div>
  </div>
);

// Simple placeholder component for incomplete routes
const PlaceholderPage = () => (
  <div className="flex items-center justify-center min-h-screen">
    <div className="text-center">
      <h1 className="text-4xl font-bold mb-4 gradient-text">Coming Soon</h1>
      <p className="text-lg text-dark-500 mb-6">This page is under construction</p>
      <a href="/" className="px-6 py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors">
        Back to Home
      </a>
    </div>
  </div>
);

function App() {
  const location = useLocation();
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Simulate initial loading
    const timer = setTimeout(() => {
      setLoading(false);
    }, 1500);

    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    // Scroll to top on route change
    window.scrollTo(0, 0);
  }, [location.pathname]);

  if (loading) {
    return <Loader />;
  }

  return (
    <AnimatePresence mode="wait">
      <Routes location={location} key={location.pathname}>
        <Route path="/" element={<MainLayout />}>
          <Route index element={<Home />} />
          <Route path="about" element={<About />} />
          <Route path="contact" element={<Contact />} />
          <Route path="careers" element={<Careers />} />
          
          {/* Blog routes */}
          <Route path="blog" element={<Blog />} />
          <Route path="blog/:slug" element={<BlogPost />} />
          
          {/* Solutions routes */}
          <Route path="solutions">
            <Route path="legal-research" element={<LegalResearch />} />
            <Route path="contract-analysis" element={<ContractAnalysis />} />
            <Route path="judgment-prediction" element={<JudgmentPrediction />} />
            <Route path="legal-chronologies" element={<LegalChronologies />} />
            <Route path="ocr-translation" element={<OCRTranslationPage />} />
            <Route path="document-automation" element={<PlaceholderPage />} />
            <Route path="case-management" element={<CaseManagement />} />
          </Route>
          
          {/* All other routes use the placeholder for now */}
          <Route path="*" element={<PlaceholderPage />} />
        </Route>
      </Routes>
    </AnimatePresence>
  );
}

export default App;